import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Card,
  CardActionArea,
  CardContent
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  LocalOffer as OptionIcon,
  Tune as TuneIcon,
  Add as AddIcon,
  ShoppingCart as CartIcon
} from '@mui/icons-material';
import { accountingApi } from '../../../../../config/apiClient';
import type { SelectedOption, SelectedExtraOption, MenuNormalItem } from '../../types/product.types';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

interface ProductMenuItem {
  id: string;
  companyId: string;
  menuId: string;
  itemId: string;
  itemName?: string;
  itemBarcode?: string;
  itemStockCode?: string;
  quantity: number;
  unit: string;
  wastagePercent: number;
  canBeNegative: boolean;
  hasRecipe: boolean;
  sortOrder: number;
  notes?: string;
  isActive: boolean;
  isOptional?: boolean;
  optionGroupId?: string;
  optionGroupName?: string;
  isDefaultOption?: boolean;
  extraOptions?: ExtraOption[];
}

interface ExtraOption {
  id: string;
  companyId: string;
  menuItemId: string;
  optionItemId: string;
  optionItemName?: string;
  optionItemUnit?: string;
  priceDifference: number;
  sortOrder: number;
  isActive: boolean;
}

interface ProductMenu {
  id: string;
  companyId: string;
  inventoryItemId: string;
  inventoryItemName?: string;
  menuName: string;
  menuCode?: string;
  description?: string;
  allowNegativeStock: boolean;
  isActive: boolean;
  menuItems?: ProductMenuItem[];
}

interface OptionGroup {
  id: string;
  name: string;
  items: ProductMenuItem[];
}

interface MenuOptionSelectionModalProps {
  open: boolean;
  onClose: () => void;
  menuProductId: string;
  menuProductName: string;
  menuPrice: number;
  companyId: string;
  onConfirm: (selections: {
    selectedOptions: SelectedOption[];
    totalPriceDifference: number;
    normalItems: MenuNormalItem[]; // ✅ Normal menü öğeleri
  }) => void;
  // ✅ YENİ: Düzenleme modu için mevcut seçimler
  initialSelections?: SelectedOption[];
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export const MenuOptionSelectionModal: React.FC<MenuOptionSelectionModalProps> = ({
  open,
  onClose,
  menuProductId,
  menuProductName,
  menuPrice,
  companyId,
  onConfirm,
  initialSelections
}) => {
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<ProductMenu | null>(null);
  const [optionGroups, setOptionGroups] = useState<OptionGroup[]>([]);
  const [normalItems, setNormalItems] = useState<ProductMenuItem[]>([]);
  
  // Seçimler
  const [selectedItems, setSelectedItems] = useState<Record<string, ProductMenuItem>>({});
  const [selectedExtraOptions, setSelectedExtraOptions] = useState<Record<string, ExtraOption | null>>({});
  
  // Ekstra opsiyon seçim modalı
  const [showExtraOptionModal, setShowExtraOptionModal] = useState(false);
  const [currentMenuItemForExtra, setCurrentMenuItemForExtra] = useState<ProductMenuItem | null>(null);

  // Menü verilerini yükle
  useEffect(() => {
    if (open && menuProductId && companyId) {
      loadMenuData();
    }
  }, [open, menuProductId, companyId]);

  const loadMenuData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await accountingApi.get(
        `/api/ProductMenu/by-item/${menuProductId}`
      );
      const menuData: ProductMenu = response.data;
      setMenu(menuData);

      // Opsiyonlu ve normal ürünleri ayır
      const items = menuData.menuItems || [];
      
      // Opsiyon gruplarını oluştur
      const groups: Record<string, OptionGroup> = {};
      const normals: ProductMenuItem[] = [];

      items.forEach(item => {
        if (item.isOptional && item.optionGroupId && item.optionGroupName) {
          if (!groups[item.optionGroupId]) {
            groups[item.optionGroupId] = {
              id: item.optionGroupId,
              name: item.optionGroupName,
              items: []
            };
          }
          groups[item.optionGroupId].items.push(item);
        } else {
          normals.push(item);
        }
      });

      setOptionGroups(Object.values(groups));
      setNormalItems(normals);

      // ✅ Seçimleri ayarla - initialSelections varsa onları kullan
      const defaultSelections: Record<string, ProductMenuItem> = {};
      const defaultExtraOptions: Record<string, ExtraOption | null> = {};
      
      Object.values(groups).forEach(group => {
        // initialSelections'dan mevcut seçimi bul
        const existingSelection = initialSelections?.find(sel => sel.optionGroupId === group.id);
        
        if (existingSelection) {
          // Düzenleme modu: mevcut seçimi kullan
          const matchingItem = group.items.find(i => i.id === existingSelection.menuItemId);
          if (matchingItem) {
            defaultSelections[group.id] = matchingItem;
            
            // Ekstra opsiyon seçimi varsa onu da ayarla
            if (existingSelection.selectedExtraOption && matchingItem.extraOptions) {
              const matchingExtra = matchingItem.extraOptions.find(
                eo => eo.id === existingSelection.selectedExtraOption?.extraOptionId
              );
              if (matchingExtra) {
                defaultExtraOptions[matchingItem.id] = matchingExtra;
              }
            }
          } else {
            // Eşleşen öğe bulunamadı, varsayılanı kullan
            const defaultItem = group.items.find(i => i.isDefaultOption) || group.items[0];
            if (defaultItem) {
              defaultSelections[group.id] = defaultItem;
            }
          }
        } else {
          // Yeni ekleme modu: varsayılanı kullan
          const defaultItem = group.items.find(i => i.isDefaultOption) || group.items[0];
          if (defaultItem) {
            defaultSelections[group.id] = defaultItem;
          }
        }
      });
      
      setSelectedItems(defaultSelections);
      setSelectedExtraOptions(defaultExtraOptions);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Opsiyon seçimi değiştiğinde
  const handleOptionChange = (groupId: string, menuItem: ProductMenuItem) => {
    setSelectedItems(prev => ({
      ...prev,
      [groupId]: menuItem
    }));
    // Önceki ekstra opsiyon seçimini temizle
    setSelectedExtraOptions(prev => ({
      ...prev,
      [menuItem.id]: null
    }));
  };

  // Ekstra opsiyon seçimi
  const handleExtraOptionSelect = (menuItemId: string, extraOption: ExtraOption | null) => {
    setSelectedExtraOptions(prev => ({
      ...prev,
      [menuItemId]: extraOption
    }));
    setShowExtraOptionModal(false);
    setCurrentMenuItemForExtra(null);
  };

  // Toplam fiyat farkını hesapla
  const calculateTotalPriceDifference = (): number => {
    let total = 0;
    Object.values(selectedExtraOptions).forEach(eo => {
      if (eo) {
        total += eo.priceDifference;
      }
    });
    return total;
  };

  // Tüm zorunlu opsiyonlar seçildi mi?
  const allOptionsSelected = (): boolean => {
    return optionGroups.every(group => selectedItems[group.id] !== undefined);
  };

  // Sepete ekle
  const handleConfirm = () => {
    if (!allOptionsSelected()) {
      setError('Lütfen tüm opsiyon gruplarından birer seçim yapın');
      return;
    }

    const selections: SelectedOption[] = [];

    // Seçilen opsiyonları ekle
    Object.entries(selectedItems).forEach(([groupId, menuItem]) => {
      const group = optionGroups.find(g => g.id === groupId);
      const extraOption = selectedExtraOptions[menuItem.id];

      selections.push({
        optionGroupId: groupId,
        optionGroupName: group?.name || '',
        menuItemId: menuItem.id,
        itemId: menuItem.itemId,
        itemName: menuItem.itemName || '',
        quantity: menuItem.quantity,
        unit: menuItem.unit,
        hasRecipe: menuItem.hasRecipe,
        selectedExtraOption: extraOption ? {
          extraOptionId: extraOption.id,
          optionItemId: extraOption.optionItemId,
          optionItemName: extraOption.optionItemName || '',
          priceDifference: extraOption.priceDifference
        } : undefined
      });
    });

    // ✅ Normal menü öğelerini de ekle
    const normalMenuItems: MenuNormalItem[] = normalItems.map(item => ({
      itemId: item.itemId,
      itemName: item.itemName || '',
      quantity: item.quantity,
      unit: item.unit
    }));

    onConfirm({
      selectedOptions: selections,
      totalPriceDifference: calculateTotalPriceDifference(),
      normalItems: normalMenuItems
    });
  };

  const totalPrice = menuPrice + calculateTotalPriceDifference();

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OptionIcon />
            <Typography variant="h6">{menuProductName}</Typography>
          </Box>
          <Chip 
            label={`${totalPrice.toFixed(2)} ₺`} 
            color="secondary" 
            sx={{ fontWeight: 'bold', fontSize: '1rem' }}
          />
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              {/* Opsiyon Grupları */}
              {optionGroups.length > 0 && (
                <Box>
                  {optionGroups.map((group, groupIndex) => (
                    <Box key={group.id} sx={{ mb: 3 }}>
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel 
                          component="legend" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: 'text.primary',
                            mb: 1
                          }}
                        >
                          {group.name}
                          <Chip 
                            label="Zorunlu" 
                            size="small" 
                            color="error" 
                            sx={{ ml: 1, height: 20, fontSize: '0.65rem' }} 
                          />
                        </FormLabel>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {group.items.map(item => {
                            const isSelected = selectedItems[group.id]?.id === item.id;
                            const hasExtraOptions = item.extraOptions && item.extraOptions.length > 0;
                            const selectedExtra = selectedExtraOptions[item.id];

                            return (
                              <Card 
                                key={item.id}
                                sx={{ 
                                  minWidth: 120,
                                  flex: '1 1 120px',
                                  border: isSelected ? '2px solid' : '1px solid',
                                  borderColor: isSelected ? 'primary.main' : 'grey.300',
                                  bgcolor: isSelected ? 'primary.50' : 'background.paper'
                                }}
                              >
                                <CardActionArea onClick={() => {
                                    // Önce seçimi yap
                                    handleOptionChange(group.id, item);
                                    
                                    // ✅ Eğer zaten seçili ve ekstra opsiyonları varsa, ekstra modalı aç
                                    // Veya ilk kez seçiliyorsa bile ekstra opsiyonları varsa modalı aç
                                    if (hasExtraOptions) {
                                      // Küçük bir gecikme ile modal aç (state güncellemesi için)
                                      setTimeout(() => {
                                        setCurrentMenuItemForExtra(item);
                                        setShowExtraOptionModal(true);
                                      }, 100);
                                    }
                                  }}>
                                  <CardContent sx={{ p: 1.5, textAlign: 'center' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                                      {isSelected && <CheckCircleIcon color="primary" fontSize="small" />}
                                    </Box>
                                    <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'}>
                                      {item.itemName}
                                    </Typography>
                                    {hasExtraOptions && (
                                      <Chip 
                                        label={selectedExtra ? `+${selectedExtra.priceDifference}₺` : 'Ekstra var'} 
                                        size="small" 
                                        color={selectedExtra ? 'info' : 'default'}
                                        sx={{ mt: 0.5, fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </CardContent>
                                </CardActionArea>
                              </Card>
                            );
                          })}
                        </Box>
                      </FormControl>
                      
                      {/* Seçilen opsiyonun ekstra seçenekleri varsa */}
                      {selectedItems[group.id] && (selectedItems[group.id].extraOptions?.length ?? 0) > 0 && (
                        <Box sx={{ mt: 1, pl: 2 }}>
                          <Button
                            size="small"
                            startIcon={<TuneIcon />}
                            onClick={() => {
                              setCurrentMenuItemForExtra(selectedItems[group.id]);
                              setShowExtraOptionModal(true);
                            }}
                            color="info"
                          >
                            {selectedExtraOptions[selectedItems[group.id].id] 
                              ? `Ekstra: ${selectedExtraOptions[selectedItems[group.id].id]?.optionItemName} (+${selectedExtraOptions[selectedItems[group.id].id]?.priceDifference}₺)`
                              : 'Ekstra Opsiyon Seç (Opsiyonel)'}
                          </Button>
                        </Box>
                      )}

                      {groupIndex < optionGroups.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Normal Ürünler (bilgilendirme) */}
              {normalItems.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Menüye Dahil:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {normalItems.map(item => (
                      <Chip 
                        key={item.id}
                        label={`${item.quantity} ${item.unit} ${item.itemName}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Fiyat Özeti */}
              {calculateTotalPriceDifference() > 0 && (
                <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Menü Fiyatı:</Typography>
                    <Typography variant="body2">{menuPrice.toFixed(2)} ₺</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="info.main">Ekstra Opsiyonlar:</Typography>
                    <Typography variant="body2" color="info.main">+{calculateTotalPriceDifference().toFixed(2)} ₺</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Toplam:</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                      {totalPrice.toFixed(2)} ₺
                    </Typography>
                  </Box>
                </Paper>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            İptal
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={loading || !allOptionsSelected()}
            startIcon={<CartIcon />}
          >
            Sepete Ekle ({totalPrice.toFixed(2)} ₺)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ekstra Opsiyon Seçim Modalı */}
      <Dialog
        open={showExtraOptionModal}
        onClose={() => setShowExtraOptionModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon color="info" />
          Ekstra Opsiyon Seç
          {currentMenuItemForExtra && (
            <Chip 
              label={currentMenuItemForExtra.itemName} 
              size="small" 
              color="primary" 
              sx={{ ml: 1 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {currentMenuItemForExtra?.extraOptions?.map(eo => {
            const isSelected = selectedExtraOptions[currentMenuItemForExtra.id]?.id === eo.id;
            return (
              <Card 
                key={eo.id}
                sx={{ 
                  mb: 1,
                  border: isSelected ? '2px solid' : '1px solid',
                  borderColor: isSelected ? 'info.main' : 'grey.300'
                }}
              >
                <CardActionArea onClick={() => handleExtraOptionSelect(currentMenuItemForExtra.id, eo)}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isSelected && <CheckCircleIcon color="info" />}
                      <Typography>{eo.optionItemName}</Typography>
                    </Box>
                    <Chip 
                      label={`+${eo.priceDifference.toFixed(2)} ₺`} 
                      size="small" 
                      color="info"
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              if (currentMenuItemForExtra) {
                handleExtraOptionSelect(currentMenuItemForExtra.id, null);
              }
            }}
            color="inherit"
          >
            Ekstra İstemiyorum
          </Button>
          <Button onClick={() => setShowExtraOptionModal(false)} color="primary">
            Tamam
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MenuOptionSelectionModal;
